<template>
  <div @click="onClickPage"
       class="vacancy-page" ref="main">
    <BasePreloader v-if="pending"/>
    <div v-else-if="error" class="vacancy-page2__error-wrap">
      <ErrorView :code="error.code"
                 :title="$tVacancy(error.message)"/>
    </div>
    <template v-else-if="vacancy">
      <Breadcrumbs
        :links="breadCrumbsLinks"
        :backRoute="backRoute"
        class="vacancy-page__breadcrumbs"
      />
      <div class="container">
        <div class="container-inner">
          <div ref="main-section"
               class="vacancy-page__main-section">
            <VacancyMain
              :vacancy="vacancy"
              :canRespondOnVacancy="canRespondOnVacancy"
              :pendingBookmarks="pendingBookmarks"
              :isMob="isMob"
              class="vacancy-page__main"
              @gallery="modalGalleryIndex = $event"
              @toggle-bookmarks="toggleBookmarks"
              @reload="loadAllData"
            />
            <VacancyMedia v-if="!isMob"
                          :title="vacancy[1].name"
                          :gallery="vacancy[3].images"
                          @onShowGallery="showImageGallery"
                          class="vacancy-page__media"/>
          </div>
          <div class="vacancy-page__secondary">
            <VacancyAboutSection
              :vacancy="vacancy"
              @click:custom-share-btn="openCopyLinkModal"
            />
            <div class="vacancy-page__side">
              <div class="vacancy-page__side-inner">
                <VacancyConditionsSection :vacancy="vacancy"/>
                <VacancyEmployerSection v-if="vacancy.employer || vacancy[3].work_place_procedures"
                                        :vacancy="vacancy"/>
                <VacancyFilesSection v-if="vacancy[3].files && vacancy[3].files.length"
                                     :files="vacancy[3].files"/>
              </div>
            </div>
            <AccentNote v-if="vacancy[3].features_3 || vacancy[3].features_3_description"
                        :title="vacancy[3].features_3"
                        :text="`<p>${vacancy[3].features_3_description}</p>`"
                        class="vacancy-page__accent-note d-none d-sm-block"/>
          </div>

          <VacancyMainRespondBtn
            v-if="canRespondOnVacancy && !vacancy.is_hidden && smOrDown"
            :vacancy="vacancy"
            style="animation: none"
            class="vacancy-btns__btn _wide-uppercase _respond mt-2 mb-4 mr-0"
          />

          <ShareToMessengers
            v-if="windowSizeMixin.width <= WINDOW_SIZE_SM"
            :title="$tVacancy('Send vacancy:')"
            :custom-links="vacancy.share_urls"
            class="vacancy-page__share"
          >
            <template v-slot:append-list>
              <span @click="openCopyLinkModal">
                <BaseSvg name="ico-link-variant" width="2rem" height="2.1rem" />
              </span>
            </template>
          </ShareToMessengers>
        </div>

        <div v-if="similarVacancies.length" class="container-inner pt-16 pb-5">
          <div class="vacancy-page__similar-vacancies-title mb-5">{{ $tVacancy('Similar vacancies') }}</div>

          <div class="vacancy-page__similar-vacancies-list">
            <VacancyCardSmall
              v-for="item in similarVacancies"
              :key="item.ID"
              :vacancy="item"
              class="vacancies-result__list-item  _small"
            />
          </div>
        </div>
      </div>
      <transition name="vacancy-fixed-panel">
        <VacancyFixedPanel v-show="showFixedPanel"
                           v-if="canRespondOnVacancy"
                           :key="tick"
                           :vacancy="vacancy"
                           :pendingBookmarks="pendingBookmarks"
                           @toggle-bookmarks="toggleBookmarks"/>
      </transition>

      <CoolLightBox v-if="Array.isArray(vacancy[3].images)"
                    :items="vacancy[3].images"
                    srcName="path"
                    :index="modalGalleryIndex"
                    @close="modalGalleryIndex = null"/>
    </template>
  </div>
</template>

<script>
  import '@/assets/less/style-vacancy-2.less'

  import {http} from "@/axios"
  import {mapActions, mapGetters} from 'vuex';
  import windowSizeMixin from '../../mixins/windowSizeMixin'
  import globalMetaMixin from '../../mixins/globalMetaMixin'
  import {WINDOW_SIZE_SM} from '@/constants/windowSizes'

  import {setMetaDataTypeJob, deleteMetaDataTypeJob} from "./common/metaDataTypeJob"
  import setAlternate from "../../router/common/setAlternate"
  import {setMetaRobots} from "@/helpers/metaRobots"
  import {PARTNER, EMPLOYEE, AGENT} from '@/constants/usersTypes'

  import ErrorView from '../../components/ErrorView'
  import Breadcrumbs from '../../components/Breadcrumbs'
  import VacancyMain from '../../components/vacancy/VacancyMain'
  import VacancyMedia from '../../components/vacancy/VacancyMedia'
  import VacancyAboutSection from '../../components/vacancy/VacancyAboutSection'
  import VacancyConditionsSection from '@/components/vacancy/VacancyConditionsSection'
  import VacancyEmployerSection from '@/components/vacancy/VacancyEmployerSection'
  import VacancyFixedPanel from '@/components/vacancy/VacancyFixedPanel'
  import VacancyFilesSection from '@/components/vacancy/VacancyFilesSection'
  import AccentNote from '@/components/vacancy/AccentNote'
  import VacancyMainRespondBtn from '../../components/vacancy/VacancyMainRespondBtn';
  import ShareToMessengers from '@/components/ShareToMessengers'
  import VacancyCardSmall from '../../components/vacancyCardSmall/VacancyCardSmall';
  import {openModalsCallBack, stopOpenModalsCallBack} from '@/services/modalsAutoShow'


  const CoolLightBox = () => import(/* webpackChunkName: "light-box" */ '../../components/CoolLightBox')


  export default {
    name: 'VacancyPage2',
    mixins: [windowSizeMixin, globalMetaMixin],
    components: {
      AccentNote,
      VacancyFilesSection,
      VacancyEmployerSection,
      VacancyConditionsSection,
      ErrorView,
      VacancyMain,
      VacancyMedia,
      Breadcrumbs,
      CoolLightBox,
      VacancyAboutSection,
      VacancyFixedPanel,
      VacancyMainRespondBtn,
      ShareToMessengers,
      VacancyCardSmall,
    },
    data() {
      return {
        tick: 0, // using for rerender on data load

        pending: false,
        fromPath: null,
        vacancy: null,
        showFixedPanel: false,
        pendingBookmarks: false,
        modalGalleryIndex: null,
        shareVacancyData: null,
        similarVacancies: [],

        WINDOW_SIZE_SM
      }
    },
    computed: {
      ...mapGetters('user', [
        'isLogged',
        'isEmployee',
      ]),

      vacancyId() {
        return this.$route.params.vacancyId
      },
      backRoute() {
        if (this.fromPath) {
          return {path: this.fromPath}
        } else {
          return {name: 'vacancies'}
        }
      },
      breadCrumbsLinks() {
        if (!this.vacancy[1].profession.category) return [];

        const links = [{
          name: this.$tVacancies('Vacancies'),
          href: '/vacancies/',
          route: {
            name: 'vacancies'
          }
        }, {
          name: this.vacancy[1].profession.category.title,
          href: '/vacancies/' + this.vacancy[1].profession.category.slug,
          route: {
            name: 'vacancies',
            params: {
              professionsIds: [this.vacancy[1].profession.category.slug]
            }
          }
        }, {
          name: this.vacancy[1].name
        }]

        return links
      },

      canRespondOnVacancy() {
        const profile = this.$store.state.user.profile;
        return !profile || [EMPLOYEE, PARTNER, AGENT].includes(profile.user_type);
      },
      isMob() {
        return this.windowSizeMixin.width <= process.env.VUE_APP_WINDOW_SIZE_MD
      },
    },
    methods: {
      ...mapActions('user', [
        'integrateReplainChat',
        'removeReplainChat',
        'integrateTidioApp',
      ]),

      loadAllData(options) {
        this.similarVacancies = [];

        return this.load(options)
          .then(() => this.loadSimilarVacancies())
          .then(() => this.loadShareVacancyData());
      },
      load(options) {
        this.error = null
        this.pending = true

        return http.get(`/api/new-vacancy/get/${this.vacancyId}`, options).then(({data}) => {
          const vacancy = data.data
          this.vacancy = vacancy

          if (vacancy.is_our && (this.isEmployee || !this.isLogged)) {
            this.integrateReplainChat();
          }

          const title = this.$tMeta('vacancy-card-title-prefix') + ' ' + vacancy[1].name + ' ' + this.$tMeta('vacancy-card-title')
          const description = vacancy[1].name + ' ' + this.$tMeta('vacancy-card-description')
          this.setMetaTitleMixin(title)
          this.setMetaDescriptionMixin(description)

          setMetaDataTypeJob(vacancy)

          this.runOpenModalCallBackWatch()

          this.setOpenGraph({
            title,
            description,
            image: vacancy[3].images[0] && vacancy[3].images[0].path,
          })

          if (vacancy.translated_locales) {
            setAlternate(this.$route, vacancy.translated_locales)
          }

          if (vacancy.index === false) {
            setMetaRobots('noindex, follow')
          }

          setTimeout(this.computeVacancyElementsToggleStyles);

          this.tick++;

          return vacancy
        }).catch((error) => {
          this.error = {
            code: error.response.status,
            message: error.response.data && error.response.data.message || error.message
          }
        }).finally(() => {
          this.pending = false
        })
      },
      async loadSimilarVacancies() {
        try {
          const { data } = await http.get(`/api/new-vacancy/similarly/${this.vacancy.ID}`);
          this.similarVacancies = data.data;
          this.tick++;
        } catch (e) {
          console.log(e);
        }
      },
      toggleBookmarks() {
        if (this.pendingBookmarks) return
        this.pendingBookmarks = true

        http.post(`/api/new-vacancy/set-to-bookmarks/${this.vacancy.ID}`).then(({data}) => {
          this.vacancy.in_bookmarks = data.data === 'SET'

          if (data.data === 'SET' && (!this.isLogged || data.bookmarks_count > 1)) {
            this.$vModal.open('saved-to-favorites', {
              bookmarksQuantity: data.bookmarks_count
            })
          }

          this.$store.commit('user/setProperty', ['bookmarksCount', data.bookmarks_count])
        }).finally(() => {
          this.pendingBookmarks = false
        })
      },
      computeVacancyElementsToggleStyles() {
        const getComputedHeight = (el) => parseFloat(getComputedStyle(el).height ?? '0');
        const elems = this.$el.querySelectorAll('.vacancy-element__toggle');

        elems.forEach(($el) => {
          const beforeLineHeight = 22;

          const childrenHeight = [...$el.childNodes].reduce((acc, childNode) => {
            if (childNode instanceof Comment) return acc;
            return acc + getComputedHeight(childNode);
          }, 0);

          const newHeight = beforeLineHeight + childrenHeight;
          const currentHeight = getComputedHeight($el);
          const finalHeight = currentHeight > newHeight ? currentHeight : newHeight;

          $el.style.maxHeight = `${finalHeight}px`;
        });
      },
      checkIsShowFixedPanel() {
        const section = this.$refs['main-section']
        if (!section) return

        this.showFixedPanel = section.getBoundingClientRect().bottom < 0
      },
      showImageGallery(index) {
        this.modalGalleryIndex = index
      },
      runOpenModalCallBackWatch() {
        stopOpenModalsCallBack()
        openModalsCallBack(10000, 'vacancy', this.canShowCallBackModal)
      },
      canShowCallBackModal() {
        return this.vacancy.is_our && !this.$vModal.isOpen('enter-phone-number');
      },
      onClickPage(event) {
        if(!event.target.closest('a, button, input, .btn')) return

        this.runOpenModalCallBackWatch()
      },
      async loadShareVacancyData() {
        try {
          const { data } = await http.get(`/api/new-vacancy/share/${this.vacancy.ID}`);
          this.shareVacancyData = data;
        } catch (e) {
          console.log(e);
        }
      },
      openCopyLinkModal() {
        const { share_link, share_message, share_message_non_html } = this.shareVacancyData;
        this.$vModal.open('copy-link-and-text', {
          title: this.$tVacancy('Share vacancy'),
          link: share_link,
          htmlText: share_message,
          htmlTextTitle: this.$tVacancy('Brief description'),
          textToCopy: share_message_non_html,
        });
      },
    },
    watch: {
      vacancyId() {
        this.loadAllData();
      }
    },
    beforeRouteEnter(to, from, next) {
      if (from.name && to.name !== from.name) {
        return next(vm => {
          vm.fromPath = from.fullPath
        })
      }

      next()
    },
    created() {
      this.loadAllData();
    },
    mounted() {
      window.addEventListener('scroll', this.checkIsShowFixedPanel)
    },
    beforeDestroy() {
      window.removeEventListener('scroll', this.checkIsShowFixedPanel)

      this.removeReplainChat();
      this.integrateTidioApp();

      deleteMetaDataTypeJob();
      stopOpenModalsCallBack();
    }
  }

</script>

<style>
</style>
